import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import queryString from 'query-string'

import { Coordinates } from '../../shared/types'
import { updateLocationIfMoved, setLocationError } from './userLocationSlice'
import { DEBOUNCE_MS, THROTTLE_MS } from './constants'
import { useLocation } from 'react-router-dom'
import { getMockUserCoordsStorage } from '../../shared/utils/loadMockUserCoords.utils'

// Debounce utility
// Debounce	Prevent sending location updates too frequently (time-based)
function debounce<T extends (...args: any[]) => void>(fn: T, delay: number) {
  let timer: NodeJS.Timeout | null = null
  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => fn(...args), delay)
  }
}

// Throttle utility
// Throttle	Prevent sending updates more than once in a fixed interval
function throttle<T extends (...args: any[]) => void>(fn: T, limit: number) {
  let lastRun = 0
  return (...args: Parameters<T>) => {
    const now = Date.now()
    if (now - lastRun >= limit) {
      lastRun = now
      fn(...args)
    }
  }
}

const useUserLocation = () => {
  const dispatch = useDispatch()

  // get query params
  const location = useLocation()
  const queryParams = queryString.parse(location.search)

  const sendLocation = useRef(
    throttle(
      debounce((coords: Coordinates) => {
        dispatch(updateLocationIfMoved(coords))
      }, DEBOUNCE_MS),
      THROTTLE_MS
    )
  )

  useEffect(() => {
    // dont use the geoLoc timer if in debug mode or using query params
    const mockLSCoords = getMockUserCoordsStorage()
    if (mockLSCoords?.latitude && mockLSCoords?.longitude) {
      sendLocation.current({
        lat: mockLSCoords.latitude,
        long: mockLSCoords.longitude,
      })
      return
    } else if (process.env.REACT_APP_USE_MOCK_COORDS === 'true') {
      const loadMockCoords = async () => {
        const { MOCK_USER_COORDS } = await import('../../mockUserCoords')
        sendLocation.current({
          lat: MOCK_USER_COORDS.latitude,
          long: MOCK_USER_COORDS.longitude,
        })
      }
      loadMockCoords()
      return
    }

    if (!navigator.geolocation) {
      dispatch(setLocationError('Geolocation is not supported'))
      return
    }

    // setup geoloc watch if not in debug mode
    const watchId = navigator.geolocation.watchPosition(
      async (pos) => {
        console.log('navigator.geolocation.watchPosition')

        const coords: Coordinates = {
          lat: pos.coords.latitude,
          long: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: pos.timestamp,
        }
        sendLocation.current(coords)
      },
      (err) => {
        dispatch(setLocationError(err.message))
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 1000,
      }
    )

    return () => navigator.geolocation.clearWatch(watchId)
  }, [dispatch])
}
export default useUserLocation
